class GlobalDrawer extends HTMLElement {
  constructor() {
    super();
    this.closeElements = this.querySelectorAll('[data-drawer-close]');
    this.previouslyFocusedElement = null;
    this.initialized = false;

    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.closeAllDrawers = this.closeAllDrawers.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    window.wetheme.webcomponentRegistry.register({ key: 'component-drawer' });
    window.eventBus.on('close:all:drawers', this.closeAllDrawers);
    this.closeElements.forEach((element) => {
      element.addEventListener('click', this.closeDrawer);
    });

    if (window.Shopify.designMode) {
      window.addEventListener('resize', this.handleResize);
    }
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    this.initialized = false;

    window.eventBus.off('close:all:drawers', this.closeAllDrawers);
    this.closeElements.forEach((element) => {
      element.removeEventListener('click', this.closeDrawer);
    });
    this.removeEventListener('keydown', this.handleKeydown);

    if (window.Shopify.designMode) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  openDrawer() {
    if (this.getAttribute('aria-hidden') === 'false') {
      this.trapFocus();
      return;
    }

    const activeElement = document.activeElement;
    this.previouslyFocusedElement =
      activeElement instanceof HTMLElement && !this.contains(activeElement) ? activeElement : null;

    window.eventBus.emit('close:all:drawers', this);
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('js-drawer-open');
    this.addEventListener('keydown', this.handleKeydown);
    window.eventBus.emit('drawer:opened', this);

    window.requestAnimationFrame(() => {
      if (this.getAttribute('aria-hidden') === 'false') this.trapFocus();
    });
  }

  closeDrawer(event) {
    event?.preventDefault();
    if (this.getAttribute('aria-hidden') === 'true') return;

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && this.contains(activeElement)) {
      activeElement.blur();
    }

    this.setAttribute('aria-hidden', 'true');
    this.removeEventListener('keydown', this.handleKeydown);
    document.body.classList.remove('js-drawer-open');
    window.eventBus.emit('drawer:closed', this);

    const focusTarget = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;
    window.requestAnimationFrame(() => {
      if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
    });
  }

  closeAllDrawers(source) {
    if (source === this || this.getAttribute('aria-hidden') === 'true') return;
    this.closeDrawer();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusableElements = this.getFocusableElements();
    if (!focusableElements.length) return;

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstFocusableElement) {
      event.preventDefault();
      lastFocusableElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      event.preventDefault();
      firstFocusableElement.focus();
    }
  }

  getFocusableElements() {
    return Array.from(
      this.querySelectorAll(
        'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.getClientRects().length > 0);
  }

  trapFocus() {
    this.getFocusableElements()[0]?.focus({ preventScroll: true });
  }

  handleResize() {
    this.classList.add('disable-transitions');
    window.setTimeout(() => {
      this.classList.remove('disable-transitions');
    }, 500);
  }
}

class CartDrawer extends GlobalDrawer {
  constructor() {
    super();
    this.dynamicContentIds = ['cart-header', 'cart-body', 'cart-footer'];
    this.scrollElement = this.querySelector('[data-cart-scroll-element]');
    this.cartEventsInitialized = false;

    this.handleCartOpen = this.handleCartOpen.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleCartRender = this.handleCartRender.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.cartEventsInitialized) return;
    this.cartEventsInitialized = true;

    window.wetheme.webcomponentRegistry.register({ key: 'component-cart-drawer' });
    window.eventBus.on('open:cart:drawer', this.handleCartOpen);
    window.eventBus.on('update:cart:drawer', this.handleCartUpdate);
    window.eventBus.on('render:cart:drawer', this.handleCartRender);
  }

  disconnectedCallback() {
    if (this.cartEventsInitialized) {
      this.cartEventsInitialized = false;
      window.eventBus.off('open:cart:drawer', this.handleCartOpen);
      window.eventBus.off('update:cart:drawer', this.handleCartUpdate);
      window.eventBus.off('render:cart:drawer', this.handleCartRender);
    }
    super.disconnectedCallback();
  }

  handleCartOpen(event) {
    if (event?.scrollToTop && this.scrollElement) {
      this.scrollElement.scrollTo(0, 0);
    }
    this.openDrawer();
  }

  handleCartUpdate(event) {
    const cartDrawerHtml = event?.sections?.['cart-drawer'];
    if (cartDrawerHtml) this.renderCartDrawer(cartDrawerHtml);
  }

  renderCartDrawer(html) {
    const updatedDrawer = new DOMParser().parseFromString(html, 'text/html');

    this.dynamicContentIds.forEach((id) => {
      const selector = `[data-cart-dynamic-content="${id}"]`;
      const updatedContent = updatedDrawer.querySelector(selector);
      const targetElement = this.querySelector(selector);
      if (updatedContent && targetElement) {
        targetElement.innerHTML = updatedContent.innerHTML;
      }
    });

    if (this.getAttribute('aria-hidden') === 'false') {
      window.requestAnimationFrame(() => {
        if (this.getAttribute('aria-hidden') === 'false') this.trapFocus();
      });
    }
  }

  async handleCartRender() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}?sections=cart-drawer`);
      const responseJson = await response.json();
      if (responseJson?.['cart-drawer']) {
        this.renderCartDrawer(responseJson['cart-drawer']);
      }
    } catch (error) {
      console.error('Failed to fetch cart drawer:', error);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
